import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import InstallAppBanner from "@/components/app/InstallAppBanner";
import {
  Bell, House, Users, ListChecks, ClockCounterClockwise, Gear, SignOut, ChatText, Receipt,
  X, ChartBar, DotsThreeOutline, Info, Sparkle, Package, Tag, EnvelopeSimple, Scales,
  CaretRight, CaretLeft,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/dashboard", label: "Home", icon: House, testid: "nav-dashboard" },
  { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
  { to: "/invoices", label: "Invoices", icon: Receipt, testid: "nav-invoices" },
  { to: "/reminders", label: "Reminders", icon: ListChecks, testid: "nav-reminders" },
  { to: "/templates", label: "Templates", icon: ChatText, testid: "nav-templates" },
  { to: "/history", label: "History", icon: ClockCounterClockwise, testid: "nav-history" },
  { to: "/settings", label: "Settings", icon: Gear, testid: "nav-settings" },
];

const OWNER_NAV = { to: "/admin", label: "Admin", icon: ChartBar, testid: "nav-admin" };

// mobile bottom nav: 4 primary + "More" drawer for the rest (+ Admin for owners)
const BOTTOM_NAV = NAV.slice(0, 4);
const MORE_NAV = NAV.slice(4); // Templates, History, Settings

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  const [legalOpen, setLegalOpen] = useState(false);

  // Close "More" sheet & submenus when navigating away
  useEffect(() => { setMoreOpen(false); setLegalOpen(false); }, [location.pathname]);

  const moreItems = user?.role === "owner" ? [...MORE_NAV, OWNER_NAV] : MORE_NAV;
  const inMoreSection = moreItems.some((i) => location.pathname.startsWith(i.to));

  // Public/company sections in the More sheet — reuse existing marketing routes
  const BUSINESS_LINKS = [
    { to: "/pricing", label: "Pricing & Plans", icon: Tag, testid: "more-pricing" },
    { to: "/features", label: "Features", icon: Sparkle, testid: "more-features" },
    { to: "/features", label: "Products", icon: Package, testid: "more-products" },
  ];
  const COMPANY_LINKS = [
    { to: "/about", label: "About DueMate", icon: Info, testid: "more-about" },
    { to: "/contact", label: "Contact Us", icon: EnvelopeSimple, testid: "more-contact" },
  ];
  const LEGAL_LINKS = [
    { to: "/privacy-policy", label: "Privacy Policy", testid: "more-privacy" },
    { to: "/terms-and-conditions", label: "Terms & Conditions", testid: "more-terms" },
    { to: "/refund-and-cancellation", label: "Refund & Cancellation", testid: "more-refund" },
    { to: "/data-deletion", label: "Data Deletion", testid: "more-data-deletion" },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 lg:pb-0" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 5rem)" }}>
      {/* Top bar */}
      <header className="border-b-2 border-black bg-[#FDFBF7] sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-14 sm:h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-logo">
            <img src="/icons/icon-192.png?v=4" alt="DueMate" className="w-9 h-9 sm:w-10 sm:h-10 rounded-md border-2 border-black shadow-[3px_3px_0px_#111111] object-cover" />
            <span className="font-heading font-black text-lg sm:text-xl tracking-tight">DueMate</span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="hidden md:flex flex-col items-end">
              <span className="text-sm font-bold leading-tight">{user?.name}</span>
              <span className="text-xs text-[#4A4A4A] font-semibold">{user?.business_name || "Set business name →"}</span>
            </div>
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-8 h-8 sm:w-10 sm:h-10 border-2 border-black rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 sm:w-10 sm:h-10 border-2 border-black rounded-full bg-[#FFD166] flex items-center justify-center font-black">
                {user?.name?.[0] || "U"}
              </div>
            )}
            <button
              data-testid="logout-button"
              onClick={logout}
              className="nb-btn bg-white px-2 sm:px-3 py-2 text-xs sm:text-sm flex items-center gap-1.5"
              title="Log out"
            >
              <SignOut size={14} weight="bold" />
              <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-4 sm:py-6 grid lg:grid-cols-[220px_1fr] gap-4 sm:gap-6">
        {/* Sidebar (desktop only) */}
        <aside className="hidden lg:block lg:sticky lg:top-24 self-start">
          <nav className="nb-card p-3 flex flex-col gap-1">
            {NAV.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={`${testid}-desktop`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md font-bold text-sm transition-colors ${
                    isActive ? "bg-[#111111] text-white" : "text-[#111111] hover:bg-[#F3F0E9]"
                  }`
                }
              >
                <Icon size={18} weight="bold" />
                {label === "Home" ? "Dashboard" : label}
              </NavLink>
            ))}
            {user?.role === "owner" && (
              <>
                <div className="h-px bg-black/10 my-2" />
                <NavLink
                  to={OWNER_NAV.to}
                  data-testid={`${OWNER_NAV.testid}-desktop`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md font-bold text-sm transition-colors ${
                      isActive ? "bg-[#111111] text-white" : "text-[#111111] hover:bg-[#F3F0E9]"
                    }`
                  }
                >
                  <OWNER_NAV.icon size={18} weight="bold" />
                  {OWNER_NAV.label}
                  <span className="ml-auto text-[9px] font-black uppercase tracking-widest bg-[#FFD166] border-2 border-black rounded px-1.5 py-0.5">Owner</span>
                </NavLink>
              </>
            )}
          </nav>
        </aside>

        <main key={location.pathname} className="anim-fade-up min-w-0">
          <InstallAppBanner />
          {children}
        </main>
      </div>

      {/* Bottom nav (mobile only) */}
      <nav
        className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t-2 border-black"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <ul className="grid grid-cols-5">
          {BOTTOM_NAV.map(({ to, label, icon: Icon, testid }) => (
            <li key={to}>
              <NavLink
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-bold uppercase tracking-wider ${
                    isActive ? "text-[#4361EE]" : "text-[#111111]"
                  }`
                }
              >
                <Icon size={22} weight="bold" />
                <span>{label}</span>
              </NavLink>
            </li>
          ))}
          <li>
            <button
              data-testid="nav-more"
              onClick={() => setMoreOpen(true)}
              className={`w-full flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-bold uppercase tracking-wider ${
                inMoreSection ? "text-[#4361EE]" : "text-[#111111]"
              }`}
            >
              <DotsThreeOutline size={22} weight="bold" />
              <span>More</span>
            </button>
          </li>
        </ul>
      </nav>

      {/* Mobile "More" bottom sheet */}
      {moreOpen && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="more-sheet">
          <div
            className="absolute inset-0 bg-black/40 anim-fade-up"
            onClick={() => setMoreOpen(false)}
            data-testid="more-backdrop"
          />
          <div
            className="absolute bottom-0 left-0 right-0 bg-[#FDFBF7] border-t-2 border-black rounded-t-2xl shadow-[0_-4px_0_#111111] p-4 pb-8 max-h-[85vh] overflow-y-auto"
            style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)" }}
          >
            {!legalOpen ? (
              <>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-black text-lg tracking-tight">More</h3>
                  <button
                    data-testid="more-close"
                    onClick={() => setMoreOpen(false)}
                    className="nb-btn bg-white px-2 py-1.5"
                    aria-label="Close"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>

                {/* Your account (existing app pages) */}
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#4A4A4A] mb-2 mt-1">Your account</p>
                <ul className="grid grid-cols-2 gap-2 mb-4">
                  {moreItems.map(({ to, label, icon: Icon, testid }) => {
                    const isOwnerItem = to === OWNER_NAV.to;
                    return (
                      <li key={to}>
                        <NavLink
                          to={to}
                          data-testid={`${testid}-mobile`}
                          className={({ isActive }) =>
                            `nb-card flex items-center gap-3 px-3 py-3 font-bold text-sm ${
                              isActive ? "bg-[#111111] text-white" : "bg-white text-[#111111]"
                            }`
                          }
                        >
                          <Icon size={18} weight="bold" />
                          <span className="flex-1">{label}</span>
                          {isOwnerItem && (
                            <span className="text-[9px] font-black uppercase tracking-widest bg-[#FFD166] text-black border-2 border-black rounded px-1.5 py-0.5">
                              Owner
                            </span>
                          )}
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>

                {/* Business */}
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#4A4A4A] mb-2">Business</p>
                <ul className="space-y-1.5 mb-4">
                  {BUSINESS_LINKS.map(({ to, label, icon: Icon, testid }) => (
                    <li key={testid}>
                      <Link to={to} data-testid={testid} className="nb-card flex items-center gap-3 px-3 py-2.5 bg-white text-sm font-bold">
                        <Icon size={16} weight="bold" />
                        <span className="flex-1">{label}</span>
                        <CaretRight size={14} weight="bold" />
                      </Link>
                    </li>
                  ))}
                </ul>

                {/* Company */}
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#4A4A4A] mb-2">Company</p>
                <ul className="space-y-1.5 mb-4">
                  {COMPANY_LINKS.map(({ to, label, icon: Icon, testid }) => (
                    <li key={testid}>
                      <Link to={to} data-testid={testid} className="nb-card flex items-center gap-3 px-3 py-2.5 bg-white text-sm font-bold">
                        <Icon size={16} weight="bold" />
                        <span className="flex-1">{label}</span>
                        <CaretRight size={14} weight="bold" />
                      </Link>
                    </li>
                  ))}
                </ul>

                {/* Legal — opens submenu */}
                <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#4A4A4A] mb-2">Legal</p>
                <button
                  data-testid="more-legal"
                  onClick={() => setLegalOpen(true)}
                  className="nb-card w-full flex items-center gap-3 px-3 py-2.5 bg-white text-sm font-bold"
                >
                  <Scales size={16} weight="bold" />
                  <span className="flex-1 text-left">Legal</span>
                  <CaretRight size={14} weight="bold" />
                </button>

                <p className="text-[10px] text-[#4A4A4A] font-semibold mt-6 text-center">
                  © DueMate · <a className="underline" href="mailto:support@duemateapp.com">support@duemateapp.com</a>
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-3">
                  <button
                    data-testid="legal-back"
                    onClick={() => setLegalOpen(false)}
                    className="nb-btn bg-white px-2 py-1.5 inline-flex items-center gap-1"
                  >
                    <CaretLeft size={14} weight="bold" />
                    <span className="text-xs font-bold">Back</span>
                  </button>
                  <h3 className="font-heading font-black text-lg tracking-tight">Legal</h3>
                  <button
                    data-testid="more-close"
                    onClick={() => setMoreOpen(false)}
                    className="nb-btn bg-white px-2 py-1.5"
                    aria-label="Close"
                  >
                    <X size={14} weight="bold" />
                  </button>
                </div>
                <ul className="space-y-1.5">
                  {LEGAL_LINKS.map(({ to, label, testid }) => (
                    <li key={testid}>
                      <Link to={to} data-testid={testid} className="nb-card flex items-center gap-3 px-3 py-3 bg-white text-sm font-bold">
                        <Scales size={16} weight="bold" />
                        <span className="flex-1">{label}</span>
                        <CaretRight size={14} weight="bold" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
